import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  Loader2,
  PackageSearch,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  SlidersHorizontal,
  Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAplSearch, useAplFilterOptions } from '../../hooks/useAplLookup';
import { reportsApi } from '../../services/api';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

const CAMPAIGN_LABELS = {
  '5A': 'OTC',
  '5D': 'Food',
  'XX': 'Not covered',
};

const formatCampaign = (code) => {
  if (!code) return '-';
  return CAMPAIGN_LABELS[code] || code;
};

export default function AplLookup() {
  const [searchInput, setSearchInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '',
    upcCode: '',
    productName: '',
    manufacturer: [],
    sku: '',
    industry: [],
    fisCat: [],
    fisSubcatDesc: [],
    campaignCode: [],
    fileName: [],
    limit: 50,
    offset: 0,
  });

  const { data: searchData, isLoading, isFetching } = useAplSearch(filters);
  const { data: filterOptionsData } = useAplFilterOptions();

  const records = searchData?.data?.records || [];
  const total = searchData?.data?.total || 0;
  const contextFilters = searchData?.data?.contextFilters || null;

  const filterOptions = filterOptionsData?.data || {};
  const defaultApplied = useRef(false);

  // Auto-select the latest APL file on first load
  useEffect(() => {
    if (!defaultApplied.current && filterOptions.latestFileName) {
      defaultApplied.current = true;
      setFilters((prev) => ({
        ...prev,
        fileName: [filterOptions.latestFileName],
      }));
    }
  }, [filterOptions.latestFileName]);

  // When a search is active and contextFilters are returned, use those
  // to narrow the dropdown options. Fall back to the full list otherwise.
  const effectiveOptions = useMemo(() => ({
    manufacturers: contextFilters?.manufacturers || filterOptions.manufacturers || [],
    industries: contextFilters?.industries || filterOptions.industries || [],
    categories: contextFilters?.categories || filterOptions.categories || [],
    subcategories: contextFilters?.subcategories || filterOptions.subcategories || [],
    fileNames: contextFilters?.fileNames || filterOptions.fileNames || [],
    campaigns: filterOptions.campaigns || [],
  }), [contextFilters, filterOptions]);

  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(total / filters.limit);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setFilters((prev) => ({
      ...prev,
      search: searchInput.trim(),
      offset: 0,
    }));
  }, [searchInput]);

  const handleMultiFilterChange = (field, values) => {
    setFilters((prev) => ({
      ...prev,
      [field]: values,
      offset: 0,
    }));
  };

  const handleAdvancedFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      offset: 0,
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    const defaultFile = filterOptions.latestFileName ? [filterOptions.latestFileName] : [];
    setFilters({
      search: '',
      upcCode: '',
      productName: '',
      manufacturer: [],
      sku: '',
      industry: [],
      fisCat: [],
      fisSubcatDesc: [],
      campaignCode: [],
      fileName: defaultFile,
      limit: 50,
      offset: 0,
    });
  };

  const toggleRow = (idx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!hasActiveFilters || isExporting) return;
    setIsExporting(true);
    try {
      const exportFilters = { ...filters, limit: 10000, offset: 0 };
      const result = await reportsApi.searchAplProducts(exportFilters);
      const rows = result?.data?.records || [];
      if (rows.length === 0) return;

      const exportData = rows.map((r) => ({
        'UPC Code': r.upc_code || '',
        'SKU': r.sku || '',
        'Campaign Code': r.campaign_code || '',
        'Campaign': formatCampaign(r.campaign_code),
        'Product Name': r.product_name || '',
        'Manufacturer': r.manufacturer || '',
        'Package Size': r.package_size || '',
        'Product Size': r.product_size || '',
        'Unit of Measurement': r.unit_of_measure || '',
        'Industry': r.industry || '',
        'FIS Category Code': r.fis_cat || '',
        'FIS Category': r.fis_cat_desc || '',
        'FIS Subcategory': r.fis_subcat_desc || '',
        'File Name': r.file_name || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'APL Products');

      const colWidths = Object.keys(exportData[0]).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      ws['!cols'] = colWidths;

      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `APL_Export_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.upcCode ||
    filters.productName ||
    filters.manufacturer.length ||
    filters.sku ||
    filters.industry.length ||
    filters.fisCat.length ||
    filters.fisSubcatDesc.length ||
    filters.campaignCode.length ||
    filters.fileName.length
  );

  const activeFilterCount =
    (filters.manufacturer.length > 0 ? 1 : 0) +
    (filters.industry.length > 0 ? 1 : 0) +
    (filters.fisCat.length > 0 ? 1 : 0) +
    (filters.fisSubcatDesc.length > 0 ? 1 : 0) +
    (filters.campaignCode.length > 0 ? 1 : 0) +
    (filters.fileName.length > 0 ? 1 : 0) +
    (filters.upcCode ? 1 : 0) +
    (filters.productName ? 1 : 0) +
    (filters.sku ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <PackageSearch className="w-6 h-6 text-blue-600" />
          FIS APL Lookup
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Search the FIS Approved Product List by UPC code, product name, or manufacturer
        </p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by UPC code, product name, manufacturer, or SKU..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={!searchInput.trim() && !hasActiveFilters}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
            showAdvanced
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
              {activeFilterCount}
            </span>
          )}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-3 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}
      </form>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Advanced Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPC Code</label>
              <input
                type="text"
                value={filters.upcCode}
                onChange={(e) => handleAdvancedFilterChange('upcCode', e.target.value)}
                placeholder="Exact or partial UPC..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
              <input
                type="text"
                value={filters.productName}
                onChange={(e) => handleAdvancedFilterChange('productName', e.target.value)}
                placeholder="Search product name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <MultiSelectDropdown
              label="Manufacturer"
              options={effectiveOptions.manufacturers}
              selected={filters.manufacturer}
              onChange={(vals) => handleMultiFilterChange('manufacturer', vals)}
              placeholder="All Manufacturers"
              searchPlaceholder="Search manufacturers..."
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input
                type="text"
                value={filters.sku}
                onChange={(e) => handleAdvancedFilterChange('sku', e.target.value)}
                placeholder="Search SKU..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <MultiSelectDropdown
              label="Industry"
              options={effectiveOptions.industries}
              selected={filters.industry}
              onChange={(vals) => handleMultiFilterChange('industry', vals)}
              placeholder="All Industries"
              searchPlaceholder="Search industries..."
            />
            <MultiSelectDropdown
              label="FIS Category"
              options={effectiveOptions.categories}
              selected={filters.fisCat}
              onChange={(vals) => handleMultiFilterChange('fisCat', vals)}
              placeholder="All Categories"
              searchPlaceholder="Search categories..."
            />
            <MultiSelectDropdown
              label="FIS Subcategory"
              options={effectiveOptions.subcategories}
              selected={filters.fisSubcatDesc}
              onChange={(vals) => handleMultiFilterChange('fisSubcatDesc', vals)}
              placeholder="All Subcategories"
              searchPlaceholder="Search subcategories..."
            />
            <MultiSelectDropdown
              label="Campaign"
              options={effectiveOptions.campaigns.map(c => ({ label: `${c}${CAMPAIGN_LABELS[c] ? ` · ${CAMPAIGN_LABELS[c]}` : ''}`, value: c }))}
              selected={filters.campaignCode}
              onChange={(vals) => handleMultiFilterChange('campaignCode', vals)}
              placeholder="All Campaigns"
              searchPlaceholder="Search campaigns..."
            />
            <MultiSelectDropdown
              label="APL File"
              options={effectiveOptions.fileNames}
              selected={filters.fileName}
              onChange={(vals) => handleMultiFilterChange('fileName', vals)}
              placeholder="All Files"
              searchPlaceholder="Search files..."
            />
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">
            {hasActiveFilters
              ? `Results (${total.toLocaleString()} products found)`
              : 'Enter a search term to find products'}
          </h3>
          <div className="flex items-center gap-3">
            {total > 0 && (
              <span className="text-sm text-gray-500">
                Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, total)}
              </span>
            )}
            {total > 0 && (
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </button>
            )}
          </div>
        </div>

        {isLoading || isFetching ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-500">Searching products...</span>
          </div>
        ) : !hasActiveFilters ? (
          <div className="text-center py-16">
            <PackageSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Search for products by UPC code, name, or manufacturer</p>
            <p className="text-sm text-gray-400 mt-1">
              Use the search bar above or open advanced filters for more options
            </p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No products found matching your search</p>
            <p className="text-sm text-gray-400 mt-1">Try broadening your search terms</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-2 py-3" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UPC Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manufacturer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit of Measurement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Industry
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FIS Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FIS Subcategory
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Name
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((row, idx) => {
                  const isExpanded = expandedRows.has(idx);
                  return (
                    <React.Fragment key={`${row.upc_code}-${idx}`}>
                      <tr
                        onClick={() => toggleRow(idx)}
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-3 text-gray-400">
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 whitespace-nowrap">
                          {row.upc_code || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'whitespace-nowrap max-w-[140px] truncate'}`}>
                          {row.sku || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'whitespace-nowrap'}`}>
                          <span className="inline-flex items-center gap-1">
                            {row.campaign_code && (
                              <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-medium shrink-0">
                                {row.campaign_code}
                              </span>
                            )}
                            <span>{formatCampaign(row.campaign_code)}</span>
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-900 ${isExpanded ? '' : 'max-w-xs truncate'}`}>
                          {row.product_name || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'max-w-[180px] truncate'}`}>
                          {row.manufacturer || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.package_size || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.product_size || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.unit_of_measure || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.industry || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'whitespace-nowrap'}`}>
                          <span className={`inline-flex items-center gap-1 ${isExpanded ? 'flex-wrap' : ''}`}>
                            {row.fis_cat && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium shrink-0">
                                {row.fis_cat}
                              </span>
                            )}
                            <span className={isExpanded ? '' : 'truncate max-w-[120px]'}>{row.fis_cat_desc || ''}</span>
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-600 ${isExpanded ? '' : 'max-w-[160px] truncate'}`}>
                          {row.fis_subcat_desc || '-'}
                        </td>
                        <td className={`px-4 py-3 text-sm text-gray-500 ${isExpanded ? '' : 'max-w-[200px] truncate'}`}>
                          {row.file_name || '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50/80">
                          <td colSpan={13} className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3 text-sm">
                              <div>
                                <span className="text-gray-500 font-medium">Campaign</span>
                                <p className="text-gray-900 mt-0.5">
                                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">{formatCampaign(row.campaign_code)}</span>
                                  {row.campaign_code && <span className="ml-1.5 text-gray-400 text-xs">({row.campaign_code})</span>}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">UPC Code</span>
                                <p className="text-gray-900 font-mono mt-0.5">{row.upc_code || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">SKU</span>
                                <p className="text-gray-900 mt-0.5 break-all">{row.sku || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Product Name</span>
                                <p className="text-gray-900 mt-0.5 break-words">{row.product_name || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Manufacturer</span>
                                <p className="text-gray-900 mt-0.5 break-words">{row.manufacturer || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Package Size</span>
                                <p className="text-gray-900 mt-0.5">{row.package_size || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Product Size</span>
                                <p className="text-gray-900 mt-0.5">{row.product_size || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Unit of Measure</span>
                                <p className="text-gray-900 mt-0.5">{row.unit_of_measure || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">Industry</span>
                                <p className="text-gray-900 mt-0.5">{row.industry || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">FIS Category</span>
                                <p className="text-gray-900 mt-0.5">
                                  {row.fis_cat && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium mr-1">{row.fis_cat}</span>}
                                  {row.fis_cat_desc || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">FIS Subcategory</span>
                                <p className="text-gray-900 mt-0.5">
                                  {row.fis_subcat_number && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium mr-1">{row.fis_subcat_number}</span>}
                                  {row.fis_subcat_desc || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 font-medium">File Name</span>
                                <p className="text-gray-900 mt-0.5 break-all">{row.file_name || '-'}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={filters.limit}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value),
                    offset: 0,
                  }))
                }
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
